import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { visuallyHidden } from '../visuallyHidden';

export type HandoffCheckStatus = 'passed' | 'warning' | 'failed';

export interface IHandoffCheck {
  label: string;
  /** Pre-formatted by the caller — a check reads as a population count, a rate or a state word. */
  value: string;
  status: HandoffCheckStatus;
}

export interface IHandoffCheckListProps {
  /**
   * Rendered exactly as given. The check set is authored by the handoff hook, so this table neither
   * pads to a fixed count nor drops a check it does not recognise.
   */
  checks: IHandoffCheck[];
  className?: string;
}

/**
 * `589:33522` draws the handoff checks as a BORDERED TABLE, not as the chip rail this shipped as:
 * 472 x 176 at (24, 391), corner radius 12, a 1px #E4E8F0 rule, holding five stacked full-width
 * rows of 470 x 35 divided by the same rule, the last 34 and undivided (589:33523 / 33526 / 33529 /
 * 33532 / 33535). The stack closes on the container exactly — 1 + 35x4 + 34 + 1 = 176 — which is
 * how the reading was checked rather than trusted.
 *
 * Rows are drawn here as a 34px content box with the divider on `& + &`, which lands the same 176
 * without asking the last row to opt out of a border every other row carries. PLA-8815.
 */
const ROW_HEIGHT = '34px';

const CheckList = styled.div``;

// `589:33521` draws this as `2 · Handoff checks` — Inter 700 / 11 / lh 100% in
// --is-color-text-primary, sentence case; the slide-out's other step heading (589:33487) matches.
const Heading = styled.h4`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['handoff-step-size']};
  font-weight: ${tokens.type['handoff-step-weight']};
  line-height: ${tokens.type['handoff-step-line']};
`;

/* The step is carried on the table, NOT on a `> * + *` rhythm on the parent: the reset below and
   such a rule are both single-class, and that tie is what silently zeroed the slide-out's other two
   steps. A longhand after the shorthand in ONE rule cannot be tied.
   Heading top 360 + its 13 box, container top 391 (589:33521 / 589:33522) = 31 top-to-top; our
   heading box is the declared 11, so the step that lands the container in the same place is 20. */
const Table = styled.dl`
  margin: 0;
  margin-top: ${tokens.space['20']};
  border: 1px solid ${tokens.color['handoff-check-rule']};
  border-radius: ${tokens.radius.md};
`;

const Row = styled.div`
  display: flex;
  min-height: ${ROW_HEIGHT};
  align-items: center;
  justify-content: space-between;
  /* The label sits 12 from the row's left edge and every verdict ENDS 12 from its right — all five
     right edges land on 458 of the row's 470, including the one whose string and width differ. */
  padding: 0 ${tokens.space['12']};

  /* The divider is border-box, so it has to be ADDED to the 34 rather than drawn inside it: at a
     flat 34 the five rows closed on 172 against the file's 176. The file puts the rule on the first
     four rows (35 each) and leaves the last at 34; putting it on rows two-to-five instead lands the
     same four rules in the same four places and the same 174 + 2 = 176. */
  & + & {
    min-height: calc(${ROW_HEIGHT} + 1px);
    border-top: 1px solid ${tokens.color['handoff-check-rule']};
  }
`;

const Label = styled.dt`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['handoff-check-size']};
  font-weight: ${tokens.type['handoff-check-label-weight']};
  line-height: ${tokens.type['handoff-check-line']};
`;

// The file draws only `passed` and a warning; `failed` takes --is-negative, which is the hue the
// slide-out already states a blocked handoff in.
const VALUE_HUE: Record<HandoffCheckStatus, string> = {
  passed: tokens.color['handoff-check-passed'],
  warning: tokens.color['handoff-check-warning'],
  failed: tokens.color.negative
};

const Value = styled.dd<{ $status: HandoffCheckStatus }>`
  margin: 0;
  color: ${({ $status }) => VALUE_HUE[$status]};
  font-size: ${tokens.type['handoff-check-size']};
  font-weight: ${tokens.type['handoff-check-value-weight']};
  line-height: ${tokens.type['handoff-check-line']};
  text-align: right;
`;

const STATUS_LABEL_KEY: Record<HandoffCheckStatus, string> = {
  passed: 'Passed',
  warning: 'Warning',
  failed: 'Failed'
};

const CheckStatusText = styled.span`
  ${visuallyHidden}
`;

const HandoffCheckList: React.FC<IHandoffCheckListProps> = ({ checks, className }) => {
  const { t } = useMultiLanguageTranslation();
  const rows = Array.isArray(checks) ? checks : [];

  if (rows.length === 0) {
    return null;
  }

  return (
    <CheckList className={className} data-testid="handoff-check-list">
      <Heading data-testid="handoff-check-list-heading">
        {t(`${TCustomerInsights}.Handoff.ChecksHeading`)}
      </Heading>
      <Table data-testid="handoff-check-table">
        {rows.map(check => (
          <Row key={check.label} data-status={check.status} data-testid="handoff-check-row">
            <Label data-testid="handoff-check-label">{check.label}</Label>
            <Value $status={check.status} data-testid="handoff-check-value">
              {check.value}
              {/* The file prints a state word here (`Passed`, `3 drivers`) so its verdict reads
                  without the hue. Ours prints a population or a date, which leaves the verdict
                  carried by colour ALONE (WCAG 1.4.1) — so it is stated here instead. */}
              <CheckStatusText data-testid="handoff-check-status">
                {t(`${TCustomerInsights}.Gates.${STATUS_LABEL_KEY[check.status]}`)}
              </CheckStatusText>
            </Value>
          </Row>
        ))}
      </Table>
    </CheckList>
  );
};

export default HandoffCheckList;
